/**
 * Cloudinary Image Optimization Utilities
 * 
 * This module provides helper functions to generate optimized Cloudinary URLs
 * with responsive transforms for different use cases.
 */

// Image variant configurations
const VARIANTS = {
  // Thumbnail for grids - fast loading, small size
  thumb: {
    mobile: 'w_480,h_480,c_fill,q_auto,f_auto,dpr_auto',
    tablet: 'w_640,h_640,c_fill,q_auto,f_auto,dpr_auto',
    desktop: 'w_400,h_400,c_fill,q_auto,f_auto,dpr_auto',
  },
  // Medium preview - for lightbox initial load
  medium: {
    mobile: 'w_800,q_auto:good,f_auto,dpr_auto',
    tablet: 'w_1200,q_auto:good,f_auto,dpr_auto',
    desktop: 'w_1400,q_auto:good,f_auto,dpr_auto',
  },
  // Full quality - for lightbox zoom/download
  full: {
    default: 'w_2200,q_auto:best,f_auto',
  },
  // Album cover - hero images
  cover: {
    mobile: 'w_600,h_400,c_fill,q_auto,f_auto,dpr_auto',
    tablet: 'w_800,h_500,c_fill,q_auto,f_auto,dpr_auto',
    desktop: 'w_1200,h_600,c_fill,q_auto,f_auto,dpr_auto',
  },
  // Tiny placeholder for blur effect
  placeholder: {
    default: 'w_50,h_50,c_fill,q_10,f_auto,e_blur:1000',
  },
};

/**
 * Check if a URL is a Cloudinary URL
 * @param {string} url - The URL to check
 * @returns {boolean}
 */
export const isCloudinaryUrl = (url) => {
  if (!url) return false;
  return url.includes('res.cloudinary.com') && url.includes('/upload/');
};

/**
 * Extract base Cloudinary URL without existing transforms
 * @param {string} url - Cloudinary URL
 * @returns {string} - Clean URL without transforms
 */
const getBaseCloudinaryUrl = (url) => {
  if (!isCloudinaryUrl(url)) return url;
  
  // Match pattern: /upload/[transforms]/[public_id]
  // We need to remove existing transforms
  const uploadIndex = url.indexOf('/upload/');
  if (uploadIndex === -1) return url;
  
  const beforeUpload = url.substring(0, uploadIndex + 8); // includes '/upload/'
  const afterUpload = url.substring(uploadIndex + 8);
  
  // Check if there are existing transforms (they contain commas or start with known transform prefixes)
  const transformPrefixes = ['w_', 'h_', 'c_', 'q_', 'f_', 'dpr_', 'e_', 'g_', 'ar_', 'fl_'];
  const parts = afterUpload.split('/');
  
  // Find where the public_id starts (skip transform segments)
  let publicIdStartIndex = 0;
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const hasTransform = transformPrefixes.some(prefix => part.includes(prefix)) || part.includes(',');
    if (!hasTransform) {
      publicIdStartIndex = i;
      break;
    }
    publicIdStartIndex = i + 1;
  }
  
  const publicId = parts.slice(publicIdStartIndex).join('/');
  return `${beforeUpload}${publicId}`;
};

/**
 * Generate an optimized Cloudinary URL with transforms
 * @param {string} url - Original image URL
 * @param {string} variant - Variant name: 'thumb', 'medium', 'full', 'cover', 'placeholder'
 * @param {string} breakpoint - Breakpoint: 'mobile', 'tablet', 'desktop', 'default'
 * @returns {string} - Optimized URL
 */
export const getOptimizedUrl = (url, variant = 'thumb', breakpoint = 'desktop') => {
  if (!url) return '';
  
  // If not a Cloudinary URL, return as-is
  if (!isCloudinaryUrl(url)) {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Relative URL - prepend backend
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    return `${BACKEND_URL}${url}`;
  }
  
  // Get base URL without existing transforms
  const baseUrl = getBaseCloudinaryUrl(url);
  
  // Get transform string for the variant and breakpoint
  const variantConfig = VARIANTS[variant];
  if (!variantConfig) {
    console.warn(`Unknown variant: ${variant}, using thumb`);
    return getOptimizedUrl(url, 'thumb', breakpoint);
  }
  
  const transforms = variantConfig[breakpoint] || variantConfig.default || variantConfig.desktop;
  if (!transforms) {
    console.warn(`No transform found for ${variant}/${breakpoint}`);
    return url;
  }
  
  // Insert transforms after /upload/
  const uploadIndex = baseUrl.indexOf('/upload/');
  const beforeUpload = baseUrl.substring(0, uploadIndex + 8);
  const afterUpload = baseUrl.substring(uploadIndex + 8);
  
  return `${beforeUpload}${transforms}/${afterUpload}`;
};

/**
 * Generate srcSet for responsive images
 * @param {string} url - Original image URL
 * @param {string} variant - Variant name
 * @returns {string} - srcSet attribute value
 */
export const getSrcSet = (url, variant = 'thumb') => {
  if (!isCloudinaryUrl(url)) return '';
  
  const breakpoints = [
    { bp: 'mobile', width: '480w' },
    { bp: 'tablet', width: '640w' },
    { bp: 'desktop', width: '900w' },
  ];
  
  return breakpoints
    .map(({ bp, width }) => `${getOptimizedUrl(url, variant, bp)} ${width}`)
    .join(', ');
};

/**
 * Generate sizes attribute for responsive images
 * @returns {string} - sizes attribute value
 */
export const getImageSizes = (type = 'grid') => {
  switch (type) {
    case 'grid':
      // For 2-col mobile, 3-col tablet, 4-col desktop grid
      return '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw';
    case 'cover':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    case 'lightbox':
      return '100vw';
    default:
      return '100vw';
  }
};

/**
 * Generate placeholder blur data URL (low-quality image)
 * @param {string} url - Original image URL
 * @returns {string} - Placeholder URL
 */
export const getPlaceholderUrl = (url) => {
  return getOptimizedUrl(url, 'placeholder', 'default');
};

/**
 * Helper to get full image URL with basic optimization
 * For backwards compatibility with existing code
 * @param {string} path - Image path or URL
 * @param {object} options - Options
 * @returns {string} - Optimized URL
 */
export const getImageUrl = (path, options = {}) => {
  if (!path) return '';
  
  const { variant = 'thumb', breakpoint = 'desktop' } = options;
  
  // Use the optimized URL generator
  return getOptimizedUrl(path, variant, breakpoint);
};

export default {
  getOptimizedUrl,
  getSrcSet,
  getImageSizes,
  getPlaceholderUrl,
  getImageUrl,
  isCloudinaryUrl,
  VARIANTS,
};
